import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Loader2, Upload, ChevronDown } from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { getError } from '../../utils';
import reservaService from '../../services/reserva.service';
import mercadopagoService from '../../services/mercadopago.service';
import paymentMethodService from '../../services/paymentMethod.service';
import { Skeleton } from '../../components/ui/skeleton';
import CheckoutStepper from '../../components/checkout/CheckoutStepper';
import { formatPaymentMethodTitle } from '../../routes/paths';
import { trackAnalyticsEvent } from '../../lib/analytics';
import { sorteosEnabledOf } from '../../lib/boardPlans';

function ConfirmarReservaSkeleton() {
  return (
    <div className='min-h-screen bg-white font-sans'>
      <div className='flex items-center gap-3 px-4 py-4 lg:px-8 lg:py-5'>
        <Skeleton className='h-9 w-9 shrink-0 rounded-full' />
        <Skeleton className='h-5 w-16' />
      </div>
      <div className='mx-auto max-w-7xl px-4 lg:px-8'>
        {/* Stepper */}
        <div className='mb-6 flex gap-1.5'>
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className='h-1 flex-1 rounded-full' />
          ))}
        </div>
        <div className='lg:grid lg:grid-cols-5 lg:gap-10'>
          {/* Left col — order summary */}
          <div className='lg:col-span-2 space-y-3'>
            <div className='rounded-xl bg-zinc-100 p-4 space-y-2'>
              <Skeleton className='h-4 w-2/3' />
              <Skeleton className='h-4 w-1/3' />
              <div className='border-t border-zinc-200 pt-2 flex justify-between'>
                <Skeleton className='h-4 w-12' />
                <Skeleton className='h-4 w-16' />
              </div>
            </div>
            <Skeleton className='hidden lg:block h-12 w-full rounded-xl' />
          </div>
          {/* Right col — payment method cards */}
          <div className='lg:col-span-3 mt-5 lg:mt-0 space-y-3'>
            <Skeleton className='h-5 w-36' />
            {[1, 2].map(i => (
              <div
                key={i}
                className='rounded-xl border border-zinc-200 px-4 py-3.5 flex items-center gap-3'
              >
                <Skeleton className='h-5 w-5 rounded-full shrink-0' />
                <div className='flex-1 space-y-1.5'>
                  <Skeleton className='h-4 w-40' />
                  <Skeleton className='h-3 w-56' />
                </div>
                <Skeleton className='h-4 w-4 rounded' />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loadingFetch: true };
    case 'FETCH_SUCCESS':
      return { ...state, reserva: action.payload, loadingFetch: false };
    case 'FETCH_FAIL':
      return { ...state, loadingFetch: false, errorFetch: action.payload };
    default:
      return state;
  }
};

/** Individual payment method radio card */
function PaymentCard({ method, selected, onSelect, children }) {
  return (
    <div
      className={`overflow-hidden rounded-xl border transition-colors ${
        selected ? 'border-ink' : 'border-divider'
      }`}
    >
      <button
        type='button'
        onClick={onSelect}
        className='flex w-full items-center gap-3 px-4 py-3.5 text-left'
      >
        {/* Radio circle */}
        <div
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
            selected ? 'border-ink' : 'border-divider'
          }`}
        >
          {selected && <div className='h-2.5 w-2.5 rounded-full bg-ink' />}
        </div>
        <div className='flex-1 min-w-0'>
          <p className='text-sm font-semibold'>{formatPaymentMethodTitle(method)}</p>
          {method.details && !selected && (
            <p className='mt-0.5 text-xs text-zinc-500 line-clamp-1'>{method.details}</p>
          )}
        </div>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-zinc-400 transition-transform ${
            selected ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Expanded details */}
      {selected && children && (
        <div className='border-t border-divider px-4 pb-4 pt-3 space-y-3'>{children}</div>
      )}
    </div>
  );
}

export default function ConfirmarReserva() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const mpStatus = searchParams.get('collection_status');
  const mpPaymentId = searchParams.get('payment_id');
  const mpPreferenceId = searchParams.get('preference_id');

  const [{ loadingFetch, reserva }, dispatch] = useReducer(reducer, {
    reserva: null,
    loadingFetch: true,
  });

  const [precioTotal, setPrecioTotal] = useState(0);
  const [loadingMp, setLoadingMp] = useState(false);
  const [loadingWire, setLoadingWire] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  // Payment methods from DB
  const [dbMethods, setDbMethods] = useState([]);
  const [loadingMethods, setLoadingMethods] = useState(true);
  const [methodsError, setMethodsError] = useState('');
  const [selectedMethodId, setSelectedMethodId] = useState(null);

  // Wire form
  const [nroTransaccion, setNroTransaccion] = useState('');
  const [comprobante, setComprobante] = useState(null);

  const fetchReserva = useCallback(async () => {
    dispatch({ type: 'FETCH_REQUEST' });
    try {
      const data = await reservaService.getReservaConTickets(id);
      dispatch({ type: 'FETCH_SUCCESS', payload: data });
      const importeReserva = Number(data.importeReserva);
      if (!Number.isNaN(importeReserva) && importeReserva > 0) {
        setPrecioTotal(importeReserva);
      } else {
        const unit = Number(data.rifa?.precioTicket) || Number(data.rifa?.precio) || 0;
        setPrecioTotal(unit * (data.cantidadTickets ?? data.tickets?.length ?? 0));
      }
    } catch (err) {
      dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
    }
  }, [id]);

  useEffect(() => {
    fetchReserva();
    setLoadingMethods(true);
    setMethodsError('');
    paymentMethodService
      .getActiveMethods()
      .then(data => {
        setDbMethods(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        console.error('Error loading payment methods:', err);
        setMethodsError(getError(err));
      })
      .finally(() => setLoadingMethods(false));
  }, [fetchReserva]);

  // MP return: always verify server-side when status=approved
  useEffect(() => {
    if (!mpPaymentId || !mpStatus || mpStatus !== 'approved') return;
    (async () => {
      try {
        setVerifying(true);
        const result = await mercadopagoService.verifyAndConfirm(mpPaymentId, id);
        setVerifyResult(result);
        if (result.confirmed) {
          await fetchReserva();
        } else if (mpPreferenceId) {
          await mercadopagoService.updateTransaction(mpPreferenceId, {
            collection_id: mpPaymentId,
            collection_status: mpStatus,
            payment_id: mpPaymentId,
          });
        }
      } catch (err) {
        console.error('Auto-verify failed:', err);
        setVerifyResult({ confirmed: false, paymentStatus: mpStatus, error: true });
      } finally {
        setVerifying(false);
      }
    })();
  }, [mpPaymentId, mpStatus, mpPreferenceId, id, fetchReserva]);

  const handleMpCheckout = async () => {
    if (!selectedMethodId) {
      toast.error('Seleccioná un método de pago');
      return;
    }
    try {
      setLoadingMp(true);
      const baseUrl = `${window.location.origin}/pago`;
      const preference = await mercadopagoService.createPreference(id, baseUrl, selectedMethodId);
      await mercadopagoService.createTransaction(
        id,
        preference.id,
        selectedMethodId,
        preference.pricing
      );
      const checkoutUrl = preference.checkoutUrl || preference.init_point;
      window.location.href = checkoutUrl;
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err.message || 'Error al conectar con MercadoPago'
      );
      setLoadingMp(false);
    }
  };

  const handleWireSubmit = async (e, method) => {
    e.preventDefault();
    if (method?.uploadAttachment && !comprobante?.[0]) {
      toast.error(t('reservation.receiptRequired'));
      return;
    }
    setLoadingWire(true);
    try {
      let comprobanteUrl;
      if (method?.uploadAttachment && comprobante?.[0]) {
        const file = comprobante[0];
        const formData = new FormData();
        const r = Math.random().toString(36).substring(7);
        formData.append('file', file, file.name.replaceAll(' ', '') + r);
        comprobanteUrl = await reservaService.uploadComprobante(formData);
      }
      await reservaService.submitPaymentProof(reserva._id, {
        ...(comprobanteUrl ? { comprobante: comprobanteUrl } : {}),
        ...(method?.invoiceNumRequired && nroTransaccion ? { nroTransaccion } : {}),
      });
      toast.success('Comprobante recibido. La compra quedará confirmada al verificar el pago.');
      await fetchReserva();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setLoadingWire(false);
    }
  };

  const handleManualVerify = async () => {
    if (!mpPaymentId) return;
    try {
      setVerifying(true);
      const result = await mercadopagoService.verifyAndConfirm(mpPaymentId, id);
      setVerifyResult(result);
      if (result.confirmed) {
        await fetchReserva();
        toast.success('¡Reserva confirmada!');
      } else {
        toast.warning(`Estado del pago: ${result.paymentStatus}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Error al verificar el pago');
    } finally {
      setVerifying(false);
    }
  };

  if (loadingFetch) return <ConfirmarReservaSkeleton />;

  const isPendiente = reserva?.estado === 'Pendiente';
  const isConfirmed = !isPendiente && reserva?.estado != null;
  const showSuccess = isConfirmed || (mpStatus === 'approved' && verifyResult?.confirmed === true);

  const ticketNumbers = Array.isArray(reserva?.tickets)
    ? reserva.tickets
    : typeof reserva?.tickets === 'string'
      ? reserva.tickets.split('-')
      : [];

  // Bonos with hidden numbers: the API omits them and only sends the quantity
  const ticketCount = reserva?.numerosOcultos
    ? Number(reserva.cantidadTickets) || 0
    : ticketNumbers.length;

  // Bonos without draws: no "Sorteo" date
  const sorteos = sorteosEnabledOf(reserva?.rifa) ? reserva?.rifa?.sorteos || [] : [];
  const now = new Date();
  const nextSorteo = sorteos.find(s => new Date(s.fecha) > now) || sorteos[sorteos.length - 1];

  const isBoard = reserva?.rifa?.tipo === 'board';
  const boardCategories = isBoard
    ? [...new Set((reserva?.casillas || []).map(c => c.categoria).filter(Boolean))]
    : [];

  const allMethods = dbMethods;
  const selectedMethod = dbMethods.find(m => m._id === selectedMethodId);
  const isMpSelected = selectedMethod?.usesPaymentGateway === true;

  /* ═══════════════════════════════════ SUCCESS STATE ═══════════════════════════════════ */
  if (showSuccess) {
    return (
      <div className='min-h-screen bg-white font-sans text-ink pb-10'>
        {/* Top bar */}
        <div className='flex items-center gap-3 px-4 py-4 lg:px-8 lg:py-5'>
          <button
            onClick={() => navigate('/')}
            className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface'
          >
            <ArrowLeft className='h-4 w-4' />
          </button>
          <span className='text-base font-semibold lg:text-lg'>Confirmación</span>
        </div>

        <div className='mx-auto max-w-7xl px-4 lg:px-8'>
          <div className='mb-6'>
            <CheckoutStepper currentStep={4} />
          </div>

          <div className='lg:grid lg:grid-cols-5 lg:gap-10 lg:items-start'>
            {/* Left: hero + image + CTAs */}
            <div className='lg:col-span-2 lg:sticky lg:top-24 space-y-5'>
              <div className='flex flex-col items-center text-center space-y-3 py-4'>
                <CheckCircle2 className='h-14 w-14 text-green-500' />
                <h1 className='text-2xl font-bold'>¡Compra exitosa!</h1>
                <p className='text-sm text-zinc-500'>
                  Muchas gracias
                  {reserva?.comprador?.nombre ? `, ${reserva.comprador.nombre}` : ''}! Tu compra fue
                  confirmada.
                </p>
              </div>

              {reserva?.rifa?.rifaImage?.fileLink && (
                <div className='flex justify-center'>
                  <img
                    src={reserva.rifa.rifaImage.fileLink}
                    alt={reserva.rifa.nombre}
                    className='h-36 w-auto rounded-xl object-contain'
                  />
                </div>
              )}

              <div className='space-y-2'>
                <button
                  type='button'
                  onClick={() => navigate('/#rifas-activas')}
                  className='w-full rounded-xl bg-ink px-4 py-3 text-center font-semibold text-white hover:opacity-90'
                >
                  Explorar más bonos
                </button>
                <button
                  type='button'
                  onClick={() => navigate('/')}
                  className='w-full rounded-xl border border-divider bg-surface px-4 py-3 text-center font-semibold text-ink hover:bg-white'
                >
                  Volver al inicio
                </button>
              </div>
            </div>

            {/* Right: ticket numbers + rifa info + email */}
            <div className='lg:col-span-3 mt-6 lg:mt-0 space-y-4'>
              {ticketNumbers.length > 0 && (
                <div className='rounded-xl bg-surface p-4 space-y-2'>
                  <p className='text-sm font-semibold'>
                    {isBoard ? 'Tus casillas' : 'Tus números'}
                  </p>
                  <div className='flex flex-wrap gap-2'>
                    {ticketNumbers.map((num, i) => (
                      <div
                        key={i}
                        className='flex h-10 w-14 items-center justify-center rounded-lg bg-ink text-sm font-bold text-white'
                      >
                        {num}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className='rounded-xl bg-surface px-4 py-3 space-y-1.5 text-sm'>
                <p className='font-semibold'>{reserva?.rifa?.nombre}</p>
                {boardCategories.length > 0 && (
                  <div className='flex flex-wrap items-center gap-1.5'>
                    <span className='text-zinc-500'>🎯 Categoría:</span>
                    {boardCategories.map(cat => (
                      <span
                        key={cat}
                        className='rounded-full bg-ink px-2.5 py-0.5 text-xs font-semibold text-white'
                      >
                        {cat}
                      </span>
                    ))}
                  </div>
                )}
                {nextSorteo?.fecha && (
                  <p className='text-zinc-500'>
                    📅 Sorteo: {moment(nextSorteo.fecha).format('DD/MM/YYYY')}
                  </p>
                )}
                {precioTotal > 0 && (
                  <p className='text-zinc-500'>
                    💳 Total pagado: <span className='font-semibold text-ink'>${precioTotal}</span>
                  </p>
                )}
              </div>

              <div className='rounded-xl border border-divider bg-surface/60 px-4 py-3'>
                <p className='text-xs text-zinc-500 text-center'>
                  📧 Recibirás un comprobante en{' '}
                  <span className='font-medium text-ink'>{reserva?.comprador?.email}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════ PAYMENT STATE ═══════════════════════════════════ */

  const mpBanner = verifying ? (
    <div className='flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700'>
      <Loader2 className='h-4 w-4 animate-spin shrink-0' />
      Verificando tu pago con MercadoPago…
    </div>
  ) : mpStatus === 'pending' ? (
    <div className='rounded-xl border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800'>
      ⏳ <strong>Pago pendiente.</strong> Confirmaremos tu reserva cuando acreditemos el pago.
    </div>
  ) : mpStatus === 'rejected' ? (
    <div className='rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800'>
      ❌ <strong>Pago rechazado.</strong> Podés intentarlo de nuevo o elegir otro método.
    </div>
  ) : null;

  /* Shared MP pay button markup */
  const mpPayButton = (
    <button
      type='button'
      disabled={loadingMp || !selectedMethodId}
      onClick={handleMpCheckout}
      className='flex w-full items-center justify-center gap-2 rounded-xl py-3 font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50'
      style={{ backgroundColor: '#009ee3' }}
    >
      {loadingMp ? (
        <>
          <Loader2 className='h-4 w-4 animate-spin' />
          Redirigiendo…
        </>
      ) : (
        `💳 Pagar con Mercado Pago — $${precioTotal}`
      )}
    </button>
  );

  return (
    <div className='min-h-screen bg-white font-sans text-ink'>
      {/* ─── Top bar ─────────────────────────────────────────── */}
      <div className='flex items-center gap-3 px-4 py-4 lg:px-8 lg:py-5'>
        <button
          onClick={() => navigate('/')}
          className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface'
          aria-label='Inicio'
        >
          <ArrowLeft className='h-4 w-4' />
        </button>
        <span className='text-base font-semibold lg:text-lg'>Pago</span>
      </div>

      {/* ─── Main content ────────────────────────────────────── */}
      <div className='mx-auto max-w-7xl px-4 pb-28 lg:px-8 lg:pb-12'>
        {/* Stepper — full width */}
        <div className='mb-6'>
          <CheckoutStepper currentStep={4} />
        </div>

        <div className='lg:grid lg:grid-cols-5 lg:items-start lg:gap-10'>
          {/* ── Left column: summary + MP button (desktop) ───── */}
          <div className='lg:col-span-2 lg:sticky lg:top-24 space-y-4'>
            {/* MP status banners */}
            {mpBanner}

            {/* Manual verify button */}
            {mpPaymentId && mpStatus === 'approved' && !verifying && !verifyResult?.confirmed && (
              <button
                type='button'
                onClick={handleManualVerify}
                disabled={verifying}
                className='w-full rounded-xl border border-divider bg-surface py-2.5 text-sm font-medium text-ink hover:bg-white'
              >
                🔄 Verificar pago
              </button>
            )}

            {/* Order summary */}
            <div className='rounded-xl bg-surface px-4 py-3 space-y-1.5'>
              <p className='text-sm font-semibold'>{reserva?.rifa?.nombre}</p>
              {ticketCount > 0 && (
                <p className='text-sm text-zinc-500'>
                  {ticketCount} número{ticketCount !== 1 ? 's' : ''}
                </p>
              )}
              <div className='flex justify-between border-t border-divider pt-2 text-sm font-bold'>
                <span>Total</span>
                <span>${precioTotal}</span>
              </div>
            </div>

            {/* MP pay button — desktop only */}
            {isPendiente && !mpPaymentId && isMpSelected && (
              <div className='hidden lg:block'>{mpPayButton}</div>
            )}
          </div>

          {/* ── Right column: payment method cards ─────────────── */}
          <div className='lg:col-span-3 mt-5 lg:mt-0'>
            {isPendiente && !mpPaymentId && (
              <div className='space-y-3'>
                <p className='text-base font-semibold'>Elegí cómo pagar</p>

                {loadingMethods && (
                  <div className='space-y-2'>
                    {[0, 1, 2].map(i => (
                      <Skeleton key={i} className='h-20 w-full rounded-xl' />
                    ))}
                  </div>
                )}

                {!loadingMethods && methodsError && (
                  <div className='rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700'>
                    <p className='font-medium'>No se pudieron cargar los medios de pago.</p>
                    <p className='mt-1 text-xs opacity-80'>{methodsError}</p>
                  </div>
                )}

                {!loadingMethods && !methodsError && allMethods.length === 0 && (
                  <div className='rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-700'>
                    <p className='font-medium'>No hay medios de pago activos.</p>
                    <p className='mt-1 text-xs text-zinc-500'>
                      Pedile al administrador que active al menos un método en
                      <span className='font-mono'> Configuración → Medios de pago</span>.
                    </p>
                  </div>
                )}

                {allMethods.map(method => (
                  <PaymentCard
                    key={method._id}
                    method={method}
                    selected={selectedMethodId === method._id}
                    onSelect={() => {
                      setSelectedMethodId(method._id);
                      trackAnalyticsEvent('payment_method_selected', {
                        rifaId: reserva?.rifa?._id,
                        ticketCount,
                        stage: 'payment',
                      });
                    }}
                  >
                    {/* ── Gateway (MercadoPago): description only; button is in left col / mobile bar ── */}
                    {method.usesPaymentGateway && (
                      <>
                        <p className='text-sm font-semibold text-ink'>
                          {formatPaymentMethodTitle(method)}
                        </p>
                        {method.details && (
                          <p className='text-sm text-zinc-500'>{method.details}</p>
                        )}
                        <p className='text-xs text-zinc-400'>
                          Al continuar serás redirigido a la pasarela de pago.
                        </p>
                        {/* Mobile inline fallback (shown when expanded on mobile only) */}
                        <div className='lg:hidden'>{mpPayButton}</div>
                      </>
                    )}

                    {/* ── Manual method: bank details + conditional fields ── */}
                    {!method.usesPaymentGateway && (
                      <form onSubmit={e => handleWireSubmit(e, method)} className='space-y-3'>
                        <p className='text-sm font-semibold text-ink'>
                          {formatPaymentMethodTitle(method)}
                        </p>
                        {method.details && (
                          <div className='rounded-lg bg-zinc-50 px-3 py-2.5 text-xs text-zinc-600 whitespace-pre-line'>
                            {method.details}
                          </div>
                        )}

                        {method.invoiceNumRequired && (
                          <div className='space-y-1'>
                            <label className='block text-xs font-medium text-zinc-600'>
                              {t('reservation.transactionNumber')}
                            </label>
                            <input
                              type='text'
                              required
                              value={nroTransaccion}
                              onChange={e => setNroTransaccion(e.target.value)}
                              className='w-full rounded-xl border border-divider bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ink/20'
                            />
                          </div>
                        )}

                        {method.uploadAttachment && (
                          <div className='space-y-1'>
                            <label className='block text-xs font-medium text-zinc-600'>
                              {t('reservation.receipt')}
                            </label>
                            <label className='flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-divider bg-surface px-3 py-2.5 text-sm hover:border-ink'>
                              <Upload className='h-4 w-4 text-zinc-400' />
                              <span className='text-zinc-500'>
                                {comprobante?.[0]?.name || 'Adjuntar comprobante'}
                              </span>
                              <input
                                type='file'
                                className='hidden'
                                required
                                onChange={e => setComprobante(e.target.files)}
                                accept='image/*,application/pdf'
                              />
                            </label>
                          </div>
                        )}

                        <button
                          type='submit'
                          disabled={loadingWire}
                          className='flex w-full items-center justify-center gap-2 rounded-xl bg-ink py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50'
                        >
                          {loadingWire ? (
                            <>
                              <Loader2 className='h-4 w-4 animate-spin' />
                              Enviando…
                            </>
                          ) : (
                            `Confirmar pago $${precioTotal}`
                          )}
                        </button>
                      </form>
                    )}
                  </PaymentCard>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Mobile-only sticky MP bar (hidden on lg+) ───────── */}
      {isPendiente && !mpPaymentId && isMpSelected && (
        <div className='fixed bottom-0 left-0 right-0 z-30 border-t border-divider bg-white px-4 py-3 lg:hidden'>
          {mpPayButton}
        </div>
      )}
    </div>
  );
}
